import { inject, Injectable, signal, untracked } from "@angular/core";
import { InterpolatableTranslationObject, InterpolationParameters, TranslateService } from "@ngx-translate/core";
import { Observable, filter, map, shareReplay, switchMap } from "rxjs";
import { TranslationLoader } from "./loaders/TranslationLoader";
import { LocalStorageService } from "../tools/services/local-storage.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({ providedIn: "root" })
export class TranslationService {
  private readonly LANGUAGE_STORAGE_KEY = "language";
  private readonly translate = inject(TranslateService);
  private readonly translationLoader = inject(TranslationLoader);
  private readonly localStorageService = inject(LocalStorageService);
  public readonly defaultLang = signal(this.translate.getDefaultLang());
  public readonly currentLang = signal(this.translate.currentLang || this.translate.defaultLang);

  onLangChange$ = this.translate.onLangChange.pipe(takeUntilDestroyed(), shareReplay(1));
  onDefaultLangChange$ = this.translate.onDefaultLangChange.pipe(takeUntilDestroyed(), shareReplay(1));

  constructor() {
    this.onLangChange$.subscribe(lang => {
      this.currentLang.set(lang.lang);
    });

    this.onDefaultLangChange$.subscribe(lang => {
      this.defaultLang.set(lang.lang);
    });
  }

  load(paths: string | string[]) {
    const loader = this.translationLoader;
    return this.waitUntilTranslationsEmits().pipe(
      switchMap(() => {
        const loadedPaths = loader.mergeResources(paths);
        return this.translate.reloadLang(this.translate.currentLang || this.translate.defaultLang).pipe(map(() => loadedPaths));
      })
    );
  }

  getUserLang(): string {
    return this.localStorageService.get(this.LANGUAGE_STORAGE_KEY) || untracked(this.defaultLang);
  }

  saveUserLang(language: string): void {
    this.use(language);
    this.setDefaultLanguage(language);
    this.localStorageService.set(this.LANGUAGE_STORAGE_KEY, language);
  }

  instant(key: string | string[], interpolateParams?: InterpolationParameters | undefined) {
    return this.translate.instant(key, interpolateParams);
  }

  setDefaultLanguage(lang: string): void {
    this.translate.setDefaultLang(lang);
  }

  getTranslateValue(value: string): Observable<string> {
    return this.translate.get(value);
  }

  use(lang: string): Observable<InterpolatableTranslationObject> {
    return this.translate.use(lang);
  }

  getLoadedKeys() {
    return this.translationLoader.getLoadedKeys();
  }

  waitUntilTranslationLoaded(pathRegex: string | RegExp) {
    return this.getLoadedKeys().pipe(filter(res => !!res.match(pathRegex)));
  }

  /**
   * Runs (after) Translations files loaded correctly
   */
  waitUntilTranslationsEmits() {
    return this.translationLoader.onResourcesLoaded;
  }
}
